/**
 * SiteLayout — Shared layout wrapper for service pages
 * Dark Tech / Glassmorphism design
 * Colors: #0c0c0c bg, #57d7e2 cyan accent, Barlow font
 * Mobile-first: all sections stack vertically on screens < 768px
 */

import React from "react";
import { Link, useLocation } from "wouter";
import NavBar from "./NavBar";
import NewsletterForm from "./NewsletterForm";
import { STRATEGIST_URL, VIDEO_REVIEW_URL } from "@/lib/cta";
import { INDEXED_WORDMARK } from "@/lib/brand";
import { VIDEOS } from "@/lib/videos";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";

const FOOTER_SERVICE_LINKS = [
  { label: "SEO Audit & Strategy", href: "/seo-audit-and-strategy" },
  { label: "Technical SEO", href: "/technical-seo" },
  { label: "AI SEO", href: "/ai-seo" },
  { label: "Local SEO", href: "/local-seo" },
  { label: "Content Production", href: "/content-production" },
  { label: "Link Building", href: "/link-building" },
  { label: "Programmatic SEO", href: "/programmatic-seo" },
  { label: "Web Design", href: "/web-design" },
  { label: "Web Development", href: "/web-development" },
];

const SOCIAL = [
  { label: "LinkedIn", href: "https://www.linkedin.com/company/indexedseo", icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M17 1H3C1.9 1 1 1.9 1 3V17C1 18.1 1.9 19 3 19H17C18.1 19 19 18.1 19 17V3C19 1.9 18.1 1 17 1ZM7 15H4.5V8H7V15ZM5.75 6.75C4.93 6.75 4.25 6.07 4.25 5.25C4.25 4.43 4.93 3.75 5.75 3.75C6.57 3.75 7.25 4.43 7.25 5.25C7.25 6.07 6.57 6.75 5.75 6.75ZM16 15H13.5V11.25C13.5 10.42 12.83 9.75 12 9.75C11.17 9.75 10.5 10.42 10.5 11.25V15H8V8H10.5V9.19C11.03 8.45 11.96 8 13 8C14.66 8 16 9.34 16 11V15Z" fill="#121212"/></svg> },
  { label: "X", href: "https://x.com/Indexed_Agency", icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M15.2 2H17.9L12.1 8.6L19 18H13.6L9.4 12.5L4.6 18H1.9L8.1 11L1.5 2H7L10.8 7L15.2 2ZM14.3 16.4H15.8L6.3 3.5H4.7L14.3 16.4Z" fill="#121212"/></svg> },
  { label: "Instagram", href: "https://www.instagram.com/indexedagency", icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 2C12.7 2 13 2 14.1 2.1C17.1 2.2 18.8 3.9 18.9 6.9C19 8 19 8.3 19 11C19 13.7 19 14 18.9 15.1C18.8 18.1 17.1 19.8 14.1 19.9C13 20 12.7 20 10 20C7.3 20 7 20 5.9 19.9C2.9 19.8 1.2 18.1 1.1 15.1C1 14 1 13.7 1 11C1 8.3 1 8 1.1 6.9C1.2 3.9 2.9 2.2 5.9 2.1C7 2 7.3 2 10 2ZM10 3.8C7.4 3.8 7.1 3.8 6 3.9C4 4 3.1 4.9 3 6.9C2.9 8 2.9 8.3 2.9 11C2.9 13.7 2.9 14 3 15.1C3.1 17.1 4 18 6 18.1C7.1 18.2 7.4 18.2 10 18.2C12.6 18.2 12.9 18.2 14 18.1C16 18 16.9 17.1 17 15.1C17.1 14 17.1 13.7 17.1 11C17.1 8.3 17.1 8 17 6.9C16.9 4.9 16 4 14 3.9C12.9 3.8 12.6 3.8 10 3.8ZM10 6.4C12 6.4 13.6 8 13.6 11C13.6 13 12 14.6 10 14.6C8 14.6 6.4 13 6.4 11C6.4 9 8 6.4 10 6.4ZM10 8.2C9 8.2 8.2 9 8.2 11C8.2 12 9 12.8 10 12.8C11 12.8 11.8 12 11.8 11C11.8 9 11 8.2 10 8.2ZM14.8 5C15.5 5 16 5.5 16 6.2C16 6.9 15.5 7.4 14.8 7.4C14.1 7.4 13.6 6.9 13.6 6.2C13.6 5.5 14.1 5 14.8 5Z" fill="#121212"/></svg> },
  { label: "Facebook", href: "https://www.facebook.com/indexedseoagency/", icon: <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M18 1H2C1.4 1 1 1.4 1 2V18C1 18.6 1.4 19 2 19H10.6V12.3H8.3V9.7H10.6V7.7C10.6 5.4 12 4.1 14.1 4.1C15.1 4.1 16 4.2 16.3 4.2V6.6H14.9C13.8 6.6 13.6 7.1 13.6 7.9V9.7H16.2L15.9 12.3H13.6V19H18C18.6 19 19 18.6 19 18V2C19 1.4 18.6 1 18 1Z" fill="#121212"/></svg> },
];

interface SiteLayoutProps {
  children: React.ReactNode;
  hideOfficeAddresses?: boolean;
  noNav?: boolean;
}

export default function SiteLayout({ children, hideOfficeAddresses, noNav }: SiteLayoutProps) {
  const [location] = useLocation();
  // Blog and newsletter pages carry their own signup form, so the footer one would be a second copy.
  const showFooterSignup = !location.startsWith("/blog") && !location.startsWith("/newsletter");
  return (
    <div style={{ background: "#0c0c0c", fontFamily: ff, minHeight: "100vh", display: "flex", flexDirection: "column" }}>

      {/* ── SHARED NAV ── */}
      {!noNav && <NavBar />}

      {/* ── PAGE CONTENT ── */}
      <main style={{ flex: 1 }}>
        {children}
      </main>

      {/* ── FOOTER ── */}
      <footer style={{ width: "100%", background: "#0c0c0c", boxSizing: "border-box", fontFamily: ff, overflowX: "hidden" }}>

        {/* Inner padded container */}
        <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "40px 20px 0", boxSizing: "border-box" }}>

          {showFooterSignup && (
            <div style={{ marginBottom: "40px" }}>
              <NewsletterForm source={`footer:${location}`} idPrefix="footer-newsletter" theme="dark" />
            </div>
          )}

          {/* ── TOP SECTION: nav columns + brand ── */}
          {/* On mobile: stacks as single column. On desktop: row with space-between */}
          <div className="footer-top-row">

            {/* Nav columns — on mobile each column is full width */}
            <div className="footer-nav-columns">
              {/* Services */}
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <a href="/services" style={{ color: "#fff", fontSize: "18px", fontWeight: 700, textDecoration: "none" }}>Services</a>
                {FOOTER_SERVICE_LINKS.map((item) => (
                  <Link key={item.label} href={item.href}
                    style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", fontWeight: 400, textDecoration: "none" }}
                  >{item.label}</Link>
                ))}
              </div>

              {/* Industries */}
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <a href="/industries" style={{ color: "#fff", fontSize: "18px", fontWeight: 700, textDecoration: "none" }}>Industries</a>
                {[
                  { label: "Law Firms", href: "/industries/seo-for-law-firms" },
                  { label: "Ecommerce", href: "/industries/seo-for-ecommerce" },
                  { label: "SaaS", href: "/industries/seo-for-saas" },
                  { label: "Healthcare", href: "/industries/seo-for-healthcare" },
                  { label: "Financial Services", href: "/industries/seo-for-financial-services" },
                  { label: "Real Estate", href: "/industries/seo-for-real-estate" },
                  { label: "Restaurants", href: "/industries/seo-for-restaurants" },
                  { label: "Construction", href: "/industries/seo-for-construction-companies" },
                ].map((item) => (
                  <a key={item.label} href={item.href}
                    style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", fontWeight: 400, textDecoration: "none" }}
                  >{item.label}</a>
                ))}
              </div>

              {/* Resources */}
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <span style={{ color: "#fff", fontSize: "18px", fontWeight: 700 }}>Resources</span>
                <a href="/blog" style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", fontWeight: 400, textDecoration: "none" }}>Blog</a>
                <a href="/glossary" style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", fontWeight: 400, textDecoration: "none" }}>Glossary</a>
                <a href="/newsletter" style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", fontWeight: 400, textDecoration: "none" }}>Newsletter</a>
                {VIDEOS.length > 0 && <a href="/videos" style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", fontWeight: 400, textDecoration: "none" }}>Videos</a>}
                <a href="/studies" style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", fontWeight: 400, textDecoration: "none" }}>Studies</a>
                <a href="/data" style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", fontWeight: 400, textDecoration: "none" }}>Data</a>
                <a href="/tools/domain-rating-checker" style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", fontWeight: 400, textDecoration: "none" }}>Domain Rating Checker</a>
                <a href="/tools/llm-visibility-checker" style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", fontWeight: 400, textDecoration: "none" }}>LLM Visibility Checker</a>
              </div>

              {/* Company */}
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <span style={{ color: "#fff", fontSize: "18px", fontWeight: 700 }}>Company</span>
                {[
                  { label: "Method", href: "/method" },
                  { label: "Work", href: "/client-stories" },
                  { label: "About Us", href: "/about" },
                  { label: "Careers", href: "/careers" },
                  // Contact is the strategist form, not the video review — it's
                  // the general "talk to a human" route rather than funnel entry.
                  { label: "Contact Us", href: STRATEGIST_URL },
                ].map((item) => (
                  <a key={item.label} href={item.href}
                    style={{ color: "rgba(255,255,255,0.7)", fontSize: "15px", fontWeight: 400, textDecoration: "none" }}
                  >{item.label}</a>
                ))}
              </div>
            </div>

            {/* Brand block */}
            <div style={{ display: "flex", flexDirection: "column", gap: "16px", maxWidth: "320px" }}>
              <img loading="lazy"
                src={INDEXED_WORDMARK}
                alt="Indexed"
                style={{ width: "160px", height: "26px", objectFit: "contain" }}
              />
              <p style={{ margin: 0, color: "rgba(255,255,255,0.55)", fontSize: "14px", lineHeight: 1.6 }}>
                We turn search into your highest ROI channel.
              </p>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                {SOCIAL.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    aria-label={`Indexed on ${s.label}`}
                    rel="noopener noreferrer"
                    target="_blank"
                    style={{
                      width: "38px", height: "38px", borderRadius: "50%",
                      background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                      textDecoration: "none", flexShrink: 0,
                    }}
                  >{s.icon}</a>
                ))}
              </div>
            </div>
          </div>

          {/* ── CTA CARD ── */}
          <div className="footer-cta-card">
            <p style={{ margin: 0, color: "#fff", fontWeight: 500, lineHeight: 1.3 }} className="footer-cta-text">
              Ready to speak with a <span style={{ color: "#57d7e2" }}>growth expert?</span>
            </p>
            <a href={VIDEO_REVIEW_URL} className="footer-cta-btn">
              Get my free video review →
            </a>
          </div>

          {/* ── ADDRESS BAR ── single office: details left, map link right */}
          {!hideOfficeAddresses && <div className="footer-address-bar">
            <div className="footer-address-item">
              <span className="footer-address-pin" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M16 7.95455C16 13.3636 9 18 9 18C9 18 2 13.3636 2 7.95455C2 6.11009 2.7375 4.34117 4.05025 3.03694C5.36301 1.73271 7.14348 1 9 1C10.8565 1 12.637 1.73271 13.9497 3.03694C15.2625 4.34117 16 6.11009 16 7.95455Z" stroke="#57d7e2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9.5 10C10.8807 10 12 8.88071 12 7.5C12 6.11929 10.8807 5 9.5 5C8.11929 5 7 6.11929 7 7.5C7 8.88071 8.11929 10 9.5 10Z" stroke="#57d7e2" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              <div className="footer-address-text">
                <span className="footer-address-city">
                  <span style={{ fontSize: "15px" }}>🇦🇪</span> Dubai, UAE
                </span>
                <span className="footer-address-line">Meydan Grandstand, Meydan Road, Nad Al Sheba</span>
              </div>
            </div>
            <a
              className="footer-address-map"
              href="https://share.google/AvRKwCYP0CI2KeICu"
              target="_blank"
              rel="noopener noreferrer"
            >
              View on map
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path d="M3 11L11 3M11 3H5M11 3V9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>}

        </div>

        {/* ── WORDMARK ── SVG text stretched to exactly 92% of the footer width
            (textLength), so it never overflows whatever font or window size
            renders it. Barlow 800: caps are 0.70em tall, "INDEXED" is 3.96em
            wide, so font-size 232 fills 920 of the 1000-unit viewBox. Hidden
            on mobile via CSS class. */}
        <div className="footer-wordmark" style={{
          width: "100%",
          borderTop: "1px solid rgba(255,255,255,0.12)",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          overflow: "hidden",
          padding: "3vw 0",
        }}>
          <svg viewBox="0 0 1000 163" width="100%" aria-hidden="true" focusable="false" style={{ display: "block", userSelect: "none" }}>
            <text
              x="40"
              y="162.5"
              textLength="920"
              lengthAdjust="spacingAndGlyphs"
              fill="#fff"
              fontSize="232"
              fontWeight={800}
              fontFamily="'Barlow', 'Barlow Fallback', sans-serif"
            >INDEXED</text>
          </svg>
        </div>

        {/* ── COPYRIGHT BAR ── */}
        <div className="footer-copyright-bar">
          <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", textAlign: "center" as const }}>© Indexed 2026 — All rights reserved.</span>
          <div style={{ display: "flex", gap: "20px", justifyContent: "center" }}>
            <a href="/privacy-policy" style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", textDecoration: "none" }}>Privacy policy</a>
            <a href="/terms-and-conditions" style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", textDecoration: "none" }}>Terms of service</a>
            <a href="/sitemap" style={{ color: "rgba(255,255,255,0.6)", fontSize: "13px", textDecoration: "none" }}>Sitemap</a>
          </div>
        </div>

      </footer>

      {/* ── FOOTER CSS ── */}
      <style>{`
        /* ===== MOBILE-FIRST FOOTER ===== */

        /* Prevent any footer element from causing horizontal scroll */
        footer, footer * {
          box-sizing: border-box;
          max-width: 100%;
        }

        /* Top section: single column stack on mobile */
        .footer-top-row {
          display: flex;
          flex-direction: column;
          gap: 36px;
          padding-bottom: 40px;
          width: 100%;
        }

        /* Nav columns: single column on mobile */
        .footer-nav-columns {
          display: flex;
          flex-direction: column;
          gap: 28px;
          width: 100%;
        }

        /* CTA card: stacked on mobile */
        .footer-cta-card {
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 24px 20px;
          border-radius: 16px;
          border: 1px solid rgba(87,215,226,0.35);
          background: rgba(255,255,255,0.02);
          margin-bottom: 32px;
          width: 100%;
        }
        .footer-cta-text {
          font-size: 22px;
        }
        .footer-cta-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 14px 24px;
          background: linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%);
          border-radius: 88px;
          border: 1px solid #57d7e2;
          text-decoration: none;
          color: #121212;
          font-size: 15px;
          font-weight: 600;
          font-family: 'Barlow', 'Barlow Fallback', sans-serif;
          box-shadow: 0 2px 12px rgba(87,215,226,0.2);
          width: 100%;
        }

        /* Address bar: single office — details + map link.
           Mobile: stacked column. Desktop: row, space-between. */
        .footer-address-bar {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 14px;
          border-top: 1px solid rgba(255,255,255,0.12);
          border-bottom: 1px solid rgba(255,255,255,0.12);
          width: 100%;
          padding: 18px 0;
        }
        .footer-address-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
        }
        .footer-address-pin {
          display: flex;
          flex-shrink: 0;
          margin-top: 1px;
        }
        .footer-address-text {
          display: flex;
          flex-direction: column;
          gap: 3px;
        }
        .footer-address-city {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 15px;
          font-weight: 600;
          color: #fff;
          line-height: 1.3;
        }
        .footer-address-line {
          font-size: 13px;
          color: rgba(255,255,255,0.55);
          line-height: 1.4;
        }
        .footer-address-map {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          font-weight: 600;
          color: #57d7e2;
          text-decoration: none;
          transition: color 0.18s ease, gap 0.18s ease;
          white-space: nowrap;
        }
        .footer-address-map:hover {
          color: #85e2e9;
          gap: 9px;
        }
        @media (min-width: 768px) {
          .footer-address-bar {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
          }
        }

        /* Wordmark: hidden on mobile, shown on desktop with borders */
        .footer-wordmark {
          display: none !important;
          width: 100%;
          overflow: hidden;
        }

        /* Copyright: centered column on mobile */
        .footer-copyright-bar {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          gap: 8px;
          padding: 16px 20px 24px;
          width: 100%;
          text-align: center;
        }

        /* ===== DESKTOP OVERRIDES (768px+) ===== */
        @media (min-width: 768px) {
          .footer-copyright-bar {
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            padding: 16px 40px 28px;
            text-align: left;
          }
          .footer-wordmark {
            display: block !important;
          }
          .footer-top-row {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }
          .footer-nav-columns {
            flex-direction: row;
            gap: 40px;
            width: auto;
          }
          .footer-cta-card {
            flex-direction: row;
            align-items: center;
            justify-content: space-between;
            padding: 28px 32px;
            width: 100%;
          }
          .footer-cta-text {
            font-size: 26px;
          }
          .footer-cta-btn {
            flex-shrink: 0;
            width: auto;
            padding: 16px 32px;
            font-size: 16px;
          }
        }
      `}</style>

    </div>
  );
}
